import { useState } from 'react';
import { useSelector } from 'react-redux';
import { ImageSquare } from '@phosphor-icons/react';
import { Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { closeSideDrawer, SpecificIcon } from '../../../utils/common/Utils';
import { CarrierLogoBoxStyled } from '../../Plans/PlanCard/PlanCardStyled';
import { EsignatureModal } from '../../Plans/EsignatureModal';
import { PCPDrawer } from '../../Plans/PlanCard/components/PCPDrawer';
import { AddYourProviderCardDetails } from './AddYourProviderCardDetails';
import { DependentDto } from '../../../queries/useBenefitPlansQuery/types';
import { AdditionalPlanElectionDataDto } from '../../Plans/PlanCard/types';
import {
  buildElectionParams,
  validatePcpDependents,
  validatePcpEmployee,
} from '../../Plans/PlanCard/utils';
import { selectPCPProviderInfoLogic } from '../../Plans/PlanCard/components/ChoosePCPDrawer/SearchResultsPCPDrawer/services';
import { selectEnrollmentContext } from '../../../../theSource/features/Home/components/EnrollmentBanner/services';
import {
  AddYourProvidersCardStyled,
  ProvidersCardHeaderStyled,
} from '../AddYourProvidersStyled';
import { AddYourProviderCardProps } from '../types';
import { getShowEsignModal } from '../../Plans/utils';
import { openEsignatureModal } from '../../Plans/EsignatureModal/tests/utils';
import { selectIsAdmin } from '../../../../theSource/features';
import { useBenefitPlansQuery } from 'enrollment/queries';
import { useConfigurationQuery } from 'theSource/queries';

export const AddYourProviderCard = ({
  benefitData,
  subscriber,
  dependents,
  updateElection,
  subscribeZipCode,
}: AddYourProviderCardProps) => {
  const { t } = useTranslation('pages.enrollmentAddProviders');

  const { configuration } = useConfigurationQuery();
  const color = 'annotation5';
  const enrollmentContext = useSelector(selectEnrollmentContext);
  const isAdmin = useSelector(selectIsAdmin);
  const benefit = benefitData?.benefit;
  const coverage = benefitData?.coverage;
  const pcpMembers = coverage?.pcp_members;
  const [additionalElectionData, setAdditionalElectionData] = useState<
    AdditionalPlanElectionDataDto | undefined
  >({});
  const [showPcpDrawer, setShowPcpDrawer] = useState(false);

  const [openChooseProviderDrawer, setOpenChooseProviderDrawer] =
    useState(false);
  const [
    hideDependentCheckboxWhenChecked,
    setHideDependentCheckboxWhenChecked,
  ] = useState<{ [key: string]: boolean }>({});

  const providerInfoLogicSelector = useSelector(selectPCPProviderInfoLogic);
  const subscriberProviderDataExistingPatient =
    providerInfoLogicSelector?.isSubscriberExistingPatientCheck;
  const subscriberProviderData =
    providerInfoLogicSelector?.subscriberProviderItems;

  const coverageDependents = coverage?.req_dependents || [];
  const selectedDependentIds =
    coverageDependents?.map(
      (dependent: DependentDto) => dependent.dependent_id
    ) || [];

  const { plansData } = useBenefitPlansQuery({
    orgBenefitId: benefitData?.benefit.org_benefit_id,
    enrollmentContextType: enrollmentContext,
    dependentIds: selectedDependentIds,
    enabled: true,
    lumosApiHost: configuration?.apiHost,
    keepPreviousData: false,
    includeContentDetails: true,
    includeEligibleDependentsData: true,
  });

  const plan = plansData?.data.plans.find(
    (planData) => planData.plan_id === coverage?.req_org_plan_id
  );

  if (plan === undefined) {
    return (
      <div className="card">
        <Skeleton
          width={820}
          height={200}
          role="status"
          aria-label="Loading..."
        />
      </div>
    );
  }

  const levelId = plan?.level_id;
  const isFindAProviderEnabled = plan?.pcp_data?.find_a_provider_enabled; //dental find a provider config

  const showEsignatureModal = getShowEsignModal(
    plan?.esign_data,
    coverage,
    isAdmin
  );
  const dependentSelectedIdPcpData = pcpMembers?.dependents?.filter(
    (dependent: any) => selectedDependentIds?.includes(dependent?.id)
  );
  const saveCheckedDependentDataItem =
    providerInfoLogicSelector?.dependentProviderItems;

  const requiredSaveCheckedDependentDataSelector =
    Array.isArray(saveCheckedDependentDataItem) &&
    saveCheckedDependentDataItem?.filter((dependent: any) =>
      selectedDependentIds?.includes(dependent?.id)
    );

  const pcpMembersSubmitDependentData =
    Object.keys(saveCheckedDependentDataItem)?.length !== 0
      ? requiredSaveCheckedDependentDataSelector
      : dependentSelectedIdPcpData;

  const subscriberProviderDataName = subscriberProviderData?.name
    ? subscriberProviderData?.name
    : pcpMembers?.pcp_name;

  const subscriberProviderDataNetworkCode = subscriberProviderData?.network_code
    ? subscriberProviderData?.network_code
    : pcpMembers?.pcp_code;

  const mappedPcpDependentData =
    pcpMembersSubmitDependentData &&
    pcpMembersSubmitDependentData?.map((data: any) => ({
      id: data?.dependent?.id ? data?.dependent?.id : data?.id,
      pcp_code: data?.providerNetworkCode
        ? data?.providerNetworkCode
        : data?.network_code
          ? data?.network_code
          : data?.pcp_code,
      pcp_name: data?.providerName
        ? data?.providerName
        : data?.name
          ? data?.name
          : data?.pcp_name,
      existing_patient: data?.existingPatient ? data?.existingPatient : false,
    }));

  const dentalPcPDataIfNotEsignature = {
    pcp_code: subscriberProviderDataNetworkCode,
    pcp_name: subscriberProviderDataName,
    existing_patient: subscriberProviderDataExistingPatient,
    dependents: mappedPcpDependentData,
  };

  const pcpDentalDataIfEsignature =
    providerInfoLogicSelector?.storePcpDentalData;

  const dentalPcPData = showEsignatureModal
    ? pcpDentalDataIfEsignature
    : dentalPcPDataIfNotEsignature;

  const togglePcpDrawer = () => {
    setShowPcpDrawer(!showPcpDrawer);
  };

  const setPcpElectionData = (data: AdditionalPlanElectionDataDto) => {
    const electionData = additionalElectionData || {};
    electionData.pcpFormData = data.pcpFormData;
    if (showEsignatureModal) {
      setAdditionalElectionData(additionalElectionData);
      openEsignatureModal(plan?.plan_id);
    } else {
      handleElection(electionData);
    }
  };

  const setEsignatureElectionData = (data: AdditionalPlanElectionDataDto) => {
    const electionData = additionalElectionData || {};
    electionData.esign_checked = data.esign_checked;
    electionData.esign_password = data.esign_password;
    handleElection(electionData);
  };

  const handleElection = (
    additionalPlanData?: AdditionalPlanElectionDataDto
  ) => {
    updateElection(
      buildElectionParams({
        plan,
        levelId,
        orgBenefitId: benefit?.org_benefit_id,
        enrollmentContextType: enrollmentContext,
        selectedDependentIds,
        additionalPlanData,
        benefitLookup: benefit?.benefit_lookup_code,
        dentalPcPData,
        isFindAProviderEnabled,
      })
    );

    setShowPcpDrawer(false);
    closeSideDrawer();
  };

  const emptyFunction = () => {
    return;
  };

  const checkSelectedCondition =
    validatePcpEmployee(pcpMembers) || validatePcpDependents(pcpMembers);

  return (
    <div
      data-testid={`plan-card-${benefit?.org_benefit_id}`}
      className="col-4 col-md-12 col-lg-12 p-0"
    >
      <AddYourProvidersCardStyled className="card planCard">
        <ProvidersCardHeaderStyled>
          <div className={`icon-container ${color}`}>
            <SpecificIcon benefitType={benefit?.benefit_lookup_code} />
          </div>
          <h3 className="pl-3_5 mb-0 d-none d-md-flex align-self-center">
            {plan?.plan_name}
          </h3>
          {plan?.carrier_logo_url && plan?.carrier_logo_url !== null ? (
            <CarrierLogoBoxStyled>
              <img
                src={plan.carrier_logo_url}
                alt={plan.carrier || t('defaultLogoAltText')}
                data-testid="carrier-logo"
                style={{ maxWidth: '100%', maxHeight: '100%' }}
              />
            </CarrierLogoBoxStyled>
          ) : (
            <CarrierLogoBoxStyled>
              <ImageSquare size={32} weight="duotone" color="#56638a" />
              <Typography color="#56638a">{plan?.carrier}</Typography>
            </CarrierLogoBoxStyled>
          )}
        </ProvidersCardHeaderStyled>
        <h2 className="d-md-none text-center mt-2_5 mb-0 mx-4">
          {plan?.plan_name}
        </h2>
        <AddYourProviderCardDetails
          coverage={coverage}
          plan={plan}
          benefit={benefit}
          subscriber={subscriber}
          dependents={dependents}
          selectedDependentIds={selectedDependentIds}
          selected={checkSelectedCondition}
          handleElection={setPcpElectionData}
          togglePcpDrawer={togglePcpDrawer}
          openChooseProviderDrawer={openChooseProviderDrawer}
          setOpenChooseProviderDrawer={setOpenChooseProviderDrawer}
          setShowPcpDrawer={setShowPcpDrawer}
          pcpMembers={pcpMembers}
          isPcpOptional={false}
          isEsignPlanRequired={showEsignatureModal || false}
          isFindAProviderEnabled={isFindAProviderEnabled}
          hideDependentCheckboxWhenChecked={hideDependentCheckboxWhenChecked}
          setHideDependentCheckboxWhenChecked={
            setHideDependentCheckboxWhenChecked
          }
          subscribeZipCode={subscribeZipCode}
        />
        {showPcpDrawer && (
          <PCPDrawer
            plan={plan}
            benefitLookup={benefit?.benefit_lookup_code}
            subscriber={subscriber}
            dependents={dependents}
            selectedDependentIds={selectedDependentIds}
            setPcpElectionData={setPcpElectionData}
            pcpMembers={pcpMembers}
            togglePcpDrawer={togglePcpDrawer}
            showPcpDrawer={showPcpDrawer}
            openChooseProviderDrawer={openChooseProviderDrawer}
            setOpenChooseProviderDrawer={setOpenChooseProviderDrawer}
            setShowPcpDrawer={setShowPcpDrawer}
            isEsignPlanRequired={showEsignatureModal || false}
            isFindAProviderEnabled={isFindAProviderEnabled}
            hideDependentCheckboxWhenChecked={hideDependentCheckboxWhenChecked}
            setHideDependentCheckboxWhenChecked={
              setHideDependentCheckboxWhenChecked
            }
            subscribeZipCode={subscribeZipCode}
            setPcpDrawerRequired={() => {
              return;
            }}
          />
        )}

        {showEsignatureModal && (
          <EsignatureModal
            setEsignElectionData={setEsignatureElectionData}
            carrierLogoUrl={plan?.carrier_logo_url}
            carrierName={plan?.carrier}
            data={plan?.esign_data}
            planId={plan?.plan_id}
            hideChoosePlanButton={true}
            pcpData={plan?.pcp_data}
            setModalRequired={emptyFunction}
            setPcpDrawerRequired={() => {
              return;
            }}
          />
        )}
      </AddYourProvidersCardStyled>
    </div>
  );
};
